import { isEqual } from 'lodash';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getHmrStoreState, initHmrStore } from '../../helpers/vite.helpers';
import { SHARED_PROJECT } from '@shared/constants';
import {
  isPublicProjectPathname,
  isReservedProjectRoutePrefix,
} from '@shared/constants/router.constants';
import { UserOrganization } from '@shared/models/user.model';

const STORE_NAME = 'organizations';

/** First pathname segment when on a public project route (`/generation/...`, `/shared/.../v/...`). */
const getOrganizationFromPathname = (): string | undefined => {
  const segment = window.location.pathname
    .split('/')[1]
    ?.replaceAll('%20', ' ');
  if (isReservedProjectRoutePrefix(segment)) return undefined;
  return segment || undefined;
};

export type IOrganizationsState = {
  organizations: string[];
  fullOrganizations: UserOrganization[];
  setOrganizations: (
    organizations: UserOrganization[],
    organization?: string,
  ) => void;

  selectedOrganization?: string;

  /**
   * Set the selected organization
   * @param organization - The organization to set
   * @param force - Whether to force the selection of the organization even if the user doesn't have access to it. Used for shared projects.
   */
  setSelectedOrganization: (organization: string) => void;
};

export const useOrganizationStore = create<IOrganizationsState>()(
  devtools(
    (set, get) =>
      ({
        organizations: [],
        fullOrganizations: [],
        selectedOrganization: undefined,
        ...getHmrStoreState(STORE_NAME),

        setOrganizations: (organizations, organization) => {
          const {
            fullOrganizations: currentOrganizations,
            selectedOrganization,
          } = get();

          // This triggers rerender in most of the app so make sure to only update if necessary
          if (isEqual(currentOrganizations, organizations)) return;

          const organizationNames = organizations.map((org) => org.name);
          const newState: Partial<IOrganizationsState> = {
            organizations: organizationNames,
            fullOrganizations: organizations,
          };

          // Verify if this should actually be done. If the selected organization is not in the list, user should automatically be redirected to 404
          // Change selected organization if it is no longer available
          const keepSharedProjectOrg =
            selectedOrganization === SHARED_PROJECT &&
            isPublicProjectPathname(window.location.pathname);

          if (
            !keepSharedProjectOrg &&
            (selectedOrganization === undefined ||
              !organizationNames.includes(selectedOrganization))
          ) {
            if (isPublicProjectPathname(window.location.pathname)) {
              newState.selectedOrganization = SHARED_PROJECT;
            } else if (
              organization &&
              organizationNames.includes(organization)
            ) {
              newState.selectedOrganization = organization;
            } else {
              const organizationFromURL = getOrganizationFromPathname();

              newState.selectedOrganization =
                organizationFromURL &&
                organizationNames.includes(organizationFromURL)
                  ? organizationFromURL
                  : organizationNames[0];
            }

            set(newState);
          }
        },
        setSelectedOrganization: (organization) => {
          const { selectedOrganization: old, organizations } = get();
          const normalized = isReservedProjectRoutePrefix(organization)
            ? SHARED_PROJECT
            : organization;
          // Always allow SHARED_PROJECT to be selected
          const allowedOrgs = new Set([...organizations, SHARED_PROJECT]);
          if (normalized !== old && allowedOrgs.has(normalized)) {
            set({ selectedOrganization: normalized });
          }
        },
      }) satisfies IOrganizationsState,
    { name: STORE_NAME },
  ),
);

initHmrStore(STORE_NAME, useOrganizationStore);
